import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { BookOpen, Search, Download } from 'lucide-react';
import { GRUPOS } from './glosarioData';

const _san = (s) => String(s || '')
  .replace(/[—–]/g, '-').replace(/≥/g, '>=').replace(/≤/g, '<=')
  .replace(/→/g, '->').replace(/[’‘]/g, "'").replace(/[“”]/g, '"')
  .replace(/ƒ/g, 'f').replace(/…/g, '...').replace(/×/g, 'x').replace(/·/g, '-');

const exportGlosarioPDF = () => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const m = 40;
  let y = m;
  const ensure = (h) => { if (y + h > pageH - m) { doc.addPage(); y = m; } };
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(15, 23, 42);
  doc.text('Glosario de KPIs', m, y); y += 8;
  doc.setDrawColor(79, 70, 229); doc.setLineWidth(2); doc.line(m, y, pageW - m, y); y += 20;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(120, 120, 130);
  doc.text('AlertGuard - Como se calcula cada metrica del modulo de KPIs', m, y); y += 22;
  GRUPOS.forEach((g) => {
    ensure(40);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(30, 30, 40);
    doc.text(_san(g.titulo), m, y); y += 6;
    doc.setDrawColor(225); doc.setLineWidth(0.5); doc.line(m, y, pageW - m, y); y += 16;
    g.terminos.forEach((x) => {
      const tLines = doc.splitTextToSize(_san(x.t), pageW - 2 * m);
      const dLines = doc.splitTextToSize(_san(x.d), pageW - 2 * m);
      const fLines = doc.splitTextToSize('f  ' + _san(x.f), pageW - 2 * m - 12);
      ensure(tLines.length * 13 + dLines.length * 12 + fLines.length * 11 + 18);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(20, 20, 30);
      doc.text(tLines, m, y); y += tLines.length * 13;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(90, 90, 100);
      doc.text(dLines, m, y); y += dLines.length * 12 + 2;
      doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(130, 130, 140);
      doc.text(fLines, m + 12, y); y += fLines.length * 11 + 10;
    });
    y += 6;
  });
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(160);
    doc.text(`Pag. ${i} de ${pages}`, pageW - m, pageH - 18, { align: 'right' });
  }
  doc.save('Glosario-KPIs.pdf');
};

const KpisGlosario = () => {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();

  const grupos = useMemo(() => {
    if (!query) return GRUPOS;
    return GRUPOS
      .map((g) => ({ ...g, terminos: g.terminos.filter((x) => (x.t + ' ' + x.d + ' ' + x.f).toLowerCase().includes(query)) }))
      .filter((g) => g.terminos.length > 0 || g.titulo.toLowerCase().includes(query));
  }, [query]);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="kpis-glosario">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10"><BookOpen className="h-6 w-6 text-amber-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Glosario</h1>
            <p className="text-sm text-slate-500">Cómo se calcula cada métrica del módulo de KPIs</p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar término o métrica…" className="pl-9" data-testid="glosario-search" />
          </div>
          <Button variant="outline" onClick={exportGlosarioPDF} className="shrink-0" data-testid="glosario-pdf-btn">
            <Download className="h-4 w-4 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {grupos.map((g) => {
          const Icon = g.icon;
          return (
            <Card key={g.key} className="p-5 lg:p-6" data-testid={`glosario-grupo-${g.key}`}>
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${g.color}1a` }}><Icon className="h-5 w-5" style={{ color: g.color }} /></div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">{g.titulo}</h2>
              </div>
              <dl className="space-y-4">
                {g.terminos.map((x, i) => (
                  <div key={i} className="border-l-2 pl-3" style={{ borderColor: g.color }} data-testid={`glosario-term-${g.key}-${i}`}>
                    <dt className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{x.t}</dt>
                    <dd className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{x.d}</dd>
                    <dd className="text-xs text-slate-400 mt-1 font-mono bg-slate-50 dark:bg-slate-800/60 rounded px-2 py-1">ƒ {x.f}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          );
        })}
        {grupos.length === 0 && (
          <div className="col-span-full py-16 text-center text-slate-400" data-testid="glosario-empty">No se encontraron términos para "{q}".</div>
        )}
      </div>
    </div>
  );
};

export default KpisGlosario;
